import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import moment from "moment";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { checkPatient, getReferredSpecialists, trackPatient } from "../../../state/actions/workflow";
import Button from "../../Shared/Button";
import { formatDateTime } from "../../../utils";
import { ROUTE_PATHS, WORKFLOW_APPT_STATUS } from "../../../constants";
import { isManualArchiveWorkflowEnabled } from "../../../infra/featureToggle";
import { splitClientAppointmentsByTab } from "../../../utils/splitClientAppointmentsByTab";
import { getTaskStatusClasses } from "../../Workflow/utils";
import TrackPatientsModal from "./TrackPatientsModal";

const NO_REFERRED_SPECIALISTS_MESSAGE = "No referred specialists found for this patient";
const UNSPECIFIED_SPECIALITY = "Unspecified speciality";

const ALREADY_TRACKED_WARNING = {
  title: "Already tracked",
  body: "This specialist is already being tracked. Track it again anyway?",
};
const NO_SPECIALIST_ALLOCATED_WARNING = {
  title: "No specialist allocated",
  body: "This referral has no allocated specialist yet. Track it without a specialist anyway?",
};

/**
 * Referrals that are already tracked or have no allocated specialist can still be
 * tracked, but the admin has to confirm first.
 */
const getTrackWarning = (referredSpecialist) => {
  if (_.get(referredSpecialist, "isTracked", false)) {
    return ALREADY_TRACKED_WARNING;
  }
  if (_.get(referredSpecialist, "isMissingClinician", false)) {
    return NO_SPECIALIST_ALLOCATED_WARNING;
  }
  return null;
};

const WORKFLOW_TAB = {
  ACTIVE: "active",
  ARCHIVED: "archived",
  HISTORY: "history",
};

// Each tracked workflow opens on the dashboard tab that actually holds it.
const WORKFLOW_TAB_META = {
  [WORKFLOW_TAB.ACTIVE]: { label: "Active workflows", path: ROUTE_PATHS.ACTIVE_WORKFLOW },
  [WORKFLOW_TAB.ARCHIVED]: { label: "Archived workflows", path: ROUTE_PATHS.ARCHIVED_WORKFLOW },
  [WORKFLOW_TAB.HISTORY]: { label: "Workflow history", path: ROUTE_PATHS.HISTORY_WORKFLOW },
};

const WORKFLOW_TAB_ORDER = [WORKFLOW_TAB.ACTIVE, WORKFLOW_TAB.ARCHIVED, WORKFLOW_TAB.HISTORY];

const buildWorkflowLink = ({ tab, workflowId }) => `${WORKFLOW_TAB_META[tab].path}?id=${workflowId}`;

const getWorkflowStatusLabel = (status) =>
  _.get(
    WORKFLOW_APPT_STATUS.find((option) => option.value === status),
    "label",
    status
  );

const toTrackedWorkflow = (tab) => (workflow) => {
  const creationTimestamp = _.get(workflow, "creationTimestamp", null);
  return {
    workflowId: _.get(workflow, "workflowId", ""),
    referralId: _.get(workflow, "referral.id", ""),
    createdAt: _.isFinite(creationTimestamp) ? moment.unix(creationTimestamp).toISOString() : "",
    status: _.get(workflow, "appointment_t.status", ""),
    tab,
  };
};

/**
 * Tracked workflows are split the same way the dashboard splits its tabs, so archived
 * and history workflows are listed too and each one keeps a link to its own tab.
 */
const getTrackedWorkflowsByReferralId = (appointments) => {
  const referredAppointments = _.filter(appointments, (appointment) => !_.isEmpty(_.get(appointment, "referral.id")));
  const {
    clientAppointments,
    archivedClientAppointments,
    historyClientAppointments,
  } = splitClientAppointmentsByTab(referredAppointments, { moveArchivedActiveRows: isManualArchiveWorkflowEnabled });

  return _.chain([
    ...clientAppointments.map(toTrackedWorkflow(WORKFLOW_TAB.ACTIVE)),
    ...archivedClientAppointments.map(toTrackedWorkflow(WORKFLOW_TAB.ARCHIVED)),
    ...historyClientAppointments.map(toTrackedWorkflow(WORKFLOW_TAB.HISTORY)),
  ])
    .groupBy("referralId")
    .mapValues((workflows) => _.orderBy(workflows, ["createdAt"], ["desc"]))
    .value();
};

const groupWorkflowsByTab = (workflows) =>
  _.chain(WORKFLOW_TAB_ORDER)
    .map((tab) => ({ tab, tabWorkflows: _.filter(workflows, { tab }) }))
    .reject(({ tabWorkflows }) => _.isEmpty(tabWorkflows))
    .value();

const addTrackedWorkflows = (referredSpecialists, appointments) => {
  const trackedWorkflowsByReferralId = getTrackedWorkflowsByReferralId(appointments);

  return referredSpecialists.map((referredSpecialist) => {
    const trackedWorkflows = trackedWorkflowsByReferralId[referredSpecialist.referralId] || [];
    return {
      ...referredSpecialist,
      // Archived and history workflows are listed, but they are no longer being tracked.
      isTracked: _.some(trackedWorkflows, { tab: WORKFLOW_TAB.ACTIVE }),
      trackedWorkflows,
    };
  });
};

const PmsTrack = ({ clientId, onError }) => {
  const [isTrackingClient, setIsTrackingClient] = useState(false);
  const [referredSpecialists, setReferredSpecialists] = useState([]);
  const [isLoadingSpecialists, setIsLoadingSpecialists] = useState(false);
  const [loadFailureReason, setLoadFailureReason] = useState(null);
  const [selectedReferralId, setSelectedReferralId] = useState("");
  const [trackWarning, setTrackWarning] = useState(null);
  const [workflowChoices, setWorkflowChoices] = useState([]);

  const loadGenerationRef = useRef(0);
  const selectedClientIdRef = useRef(clientId);

  const specialistProfiles = useSelector((state) => state.app.specialistProfiles);

  const resetSelection = useCallback(() => {
    setReferredSpecialists([]);
    setSelectedReferralId("");
    setLoadFailureReason(null);
    setTrackWarning(null);
    setWorkflowChoices([]);
  }, []);

  const loadReferredSpecialists = useCallback(
    async (patientId) => {
      const generation = loadGenerationRef.current + 1;
      loadGenerationRef.current = generation;

      setIsLoadingSpecialists(true);
      resetSelection();

      const [specialistsResult, workflowsResult] = await Promise.all([
        getReferredSpecialists({ clientId: patientId }),
        checkPatient({ clientId: patientId }),
      ]);

      if (loadGenerationRef.current !== generation) {
        return;
      }

      if (specialistsResult.hasError) {
        setLoadFailureReason(specialistsResult.errorText);
        setIsLoadingSpecialists(false);
        return;
      }
      if (workflowsResult.hasError) {
        setLoadFailureReason("Checking existing workflows failed. Please try again.");
        setIsLoadingSpecialists(false);
        return;
      }

      const specialists = specialistsResult.referredSpecialists;
      const appointments = workflowsResult.appointments;
      const specialistsWithTrackedWorkflows = addTrackedWorkflows(specialists, appointments);
      setReferredSpecialists(specialistsWithTrackedWorkflows);

      // A single referral still has to be confirmed by the admin, so it is only pre-selected
      if (specialistsWithTrackedWorkflows.length === 1) {
        setSelectedReferralId(specialistsWithTrackedWorkflows[0].referralId);
      }
      setIsLoadingSpecialists(false);
    },
    [resetSelection]
  );

  useEffect(() => {
    selectedClientIdRef.current = clientId;

    if (_.isEmpty(clientId)) {
      setIsLoadingSpecialists(false);
      resetSelection();
      return undefined;
    }
    loadReferredSpecialists(clientId);

    return () => {
      loadGenerationRef.current += 1;
    };
  }, [clientId, loadReferredSpecialists, resetSelection]);

  const trackSelectedPatient = async () => {
    const trackedClientId = clientId;
    onError(null);
    setIsTrackingClient(true);
    const { hasError, errorText = "" } = await trackPatient({
      clientId: trackedClientId,
      referralId: selectedReferralId,
    });
    setIsTrackingClient(false);

    if (selectedClientIdRef.current !== trackedClientId) {
      return;
    }

    if (hasError) {
      onError(errorText);
      return;
    }

    await loadReferredSpecialists(trackedClientId);
  };

  const handleTrackPatient = async () => {
    const selectedReferredSpecialist = _.find(referredSpecialists, { referralId: selectedReferralId }) || null;
    const warning = getTrackWarning(selectedReferredSpecialist);
    if (warning) {
      setTrackWarning(warning);
      return;
    }
    await trackSelectedPatient();
  };

  const handleTrackAnyway = async () => {
    setTrackWarning(null);
    await trackSelectedPatient();
  };

  const handleCloseModal = () => {
    setTrackWarning(null);
  };

  const handleCloseWorkflowPicker = () => {
    setWorkflowChoices([]);
  };

  // Duplicate workflows are allowed, so having more than one prompts a choice.
  const renderTrackedWorkflowLink = (referredSpecialist) => {
    const trackedWorkflows = _.get(referredSpecialist, "trackedWorkflows", []);
    if (_.isEmpty(trackedWorkflows)) {
      return null;
    }

    const workflowCount = trackedWorkflows.length;
    const hasMultipleWorkflows = workflowCount > 1;
    const linkContent = (
      <>
        <span className="icon">
          <i className="fas fa-link"></i>
        </span>
        {hasMultipleWorkflows && <span className="has-text-weight-semibold">{workflowCount}</span>}
      </>
    );

    if (!hasMultipleWorkflows) {
      return (
        <Link
          className="has-text-link is-flex is-align-items-center"
          to={buildWorkflowLink(trackedWorkflows[0])}
          title="Open the tracked workflow"
          onClick={(e) => e.stopPropagation()}
        >
          {linkContent}
        </Link>
      );
    }

    return (
      <a
        className="has-text-link is-flex is-align-items-center"
        href="#open-tracked-workflow"
        title={`Choose which of ${workflowCount} tracked workflows to open`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setWorkflowChoices(trackedWorkflows);
        }}
      >
        {linkContent}
      </a>
    );
  };

  const getSpecialityName = (referredSpecialist) => {
    if (!_.isEmpty(referredSpecialist.speciality)) {
      return referredSpecialist.speciality;
    }
    return specialistProfiles.find(({ id }) => id === referredSpecialist.practitionerId)?.specialties?.[0]?.name || "";
  };

  const referredSpecialistsBySpeciality = useMemo(() => {
    const groups = _.groupBy(referredSpecialists, (referredSpecialist) => {
      const specialityName = getSpecialityName(referredSpecialist);
      return _.isEmpty(specialityName) ? UNSPECIFIED_SPECIALITY : specialityName;
    });

    return _.sortBy(Object.entries(groups), ([specialityName]) =>
      specialityName === UNSPECIFIED_SPECIALITY ? "\uffff" : specialityName.toLowerCase()
    ).map(([specialityName, specialists]) => ({
      specialityName,
      specialists,
    }));
    // getSpecialityName closes over specialistProfiles; both deps are listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referredSpecialists, specialistProfiles]);

  const hasSelectedPatient = !_.isEmpty(clientId);
  const hasNoReferredSpecialists =
    hasSelectedPatient && !isLoadingSpecialists && _.isNil(loadFailureReason) && referredSpecialists.length === 0;

  return (
    <>
      {!hasSelectedPatient && (
        <p className="has-text-grey is-size-7">Select a client above to view their referred specialists.</p>
      )}
      {hasSelectedPatient && <p className="menu-label">REFERRED SPECIALISTS</p>}
      {isLoadingSpecialists && <p className="is-size-7">Loading referred specialists ...</p>}
      {loadFailureReason && <p className="has-text-danger is-size-7">{loadFailureReason}</p>}
      {hasNoReferredSpecialists && <p className="has-text-danger is-size-7">{NO_REFERRED_SPECIALISTS_MESSAGE}</p>}
      {referredSpecialists.length > 0 && (
        <div className="table-container apt-table track-patients-referrals">
          <table className="table is-fullwidth is-stripe is-hoverable is-scrollable is-size-7">
            <thead>
              <tr>
                <th>Specialist</th>
                <th>Referral date</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {referredSpecialistsBySpeciality.map(({ specialityName, specialists }) => (
                <React.Fragment key={specialityName}>
                  <tr className="has-background-light">
                    <td colSpan={3} className="has-text-weight-semibold">
                      {specialityName}
                    </td>
                  </tr>
                  {specialists.map((referredSpecialist) => (
                    <tr
                      key={referredSpecialist.referralId}
                      className={`${
                        referredSpecialist.referralId === selectedReferralId ? "is-active " : ""
                      }is-hovered`}
                      onClick={() => {
                        setTrackWarning(null);
                        setSelectedReferralId(referredSpecialist.referralId);
                      }}
                    >
                      <td>{referredSpecialist.practitionerName}</td>
                      <td>{formatDateTime(referredSpecialist.startDate, false, false)}</td>
                      <td>{renderTrackedWorkflowLink(referredSpecialist)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TrackPatientsModal
        isOpen={Boolean(trackWarning)}
        onClose={handleCloseModal}
        title={trackWarning?.title}
        description={trackWarning?.body}
        actions={[
          { text: "Cancel", onClick: handleCloseModal },
          { text: "Track Anyway", onClick: handleTrackAnyway, primary: true },
        ]}
      />
      <TrackPatientsModal
        isOpen={!_.isEmpty(workflowChoices)}
        onClose={handleCloseWorkflowPicker}
        title="Open tracked workflow"
        description={`This referral has ${workflowChoices.length} workflows. Choose one to open.`}
      >
        <div className="track-workflow-choices">
          <div className="track-workflow-choices-header">
            <span>Bookings</span>
            <span>Creation DT</span>
            <span />
          </div>
          <div className="track-workflow-choices-body">
            {groupWorkflowsByTab(workflowChoices).map(({ tab, tabWorkflows }) => (
              <div key={tab} className="track-workflow-choice-group">
                <h3 className="track-workflow-choice-group-label">
                  {WORKFLOW_TAB_META[tab].label}
                  <span className="track-workflow-choice-group-count">{tabWorkflows.length}</span>
                </h3>
                <ul className="track-workflow-choice-list">
                  {tabWorkflows.map((workflow) => (
                    <li key={workflow.workflowId}>
                      <Link className="track-workflow-choice" to={buildWorkflowLink(workflow)}>
                        <span>
                          <span className={`task-btn px-2 ${getTaskStatusClasses(workflow.status)}`}>
                            {getWorkflowStatusLabel(workflow.status)}
                          </span>
                        </span>
                        <span className="track-workflow-choice-created">{formatDateTime(workflow.createdAt)}</span>
                        <span className="icon is-small track-workflow-choice-chevron">
                          <i className="fas fa-chevron-right" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </TrackPatientsModal>
      <div className="field">
        <div className="control">
          <Button
            text="TRACK SELECTED CLIENT"
            isLoading={isTrackingClient}
            onClick={handleTrackPatient}
            disabled={!hasSelectedPatient || isLoadingSpecialists || _.isEmpty(selectedReferralId)}
          />
        </div>
      </div>
    </>
  );
};

export default PmsTrack;
